import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Search from "../../../../../../assets/media/svgs/Search";
import { GeneralObject } from "../../../../../../definitions/general";
import {
  useUnauthenticatedApi,
  useZoUnauthenticatedApi,
} from "../../../../../../hooks/useApi";
import useInput from "../../../../../../hooks/useInput";
import { gtag } from "../../../../../../utils/track";
import { trackZostelEvent } from "../../../../../../utils/tracking";

interface SearchBarHeaderProps {}

type LinkType = {
  id: string;
  destinationSlug: string;
  title: string;
  slug?: string;
};

const slugify = (input: string): string => {
  return input
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .toLowerCase();
};

const formatLinks: (operators: GeneralObject[]) => LinkType[] = (operators) => {
  return operators
    .map((o) => {
      return {
        id: o.slug,
        destinationSlug: `${o.destination?.slug}/${o.slug}`,
        title: o.name,
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title));
};

const SearchInput: React.FC<{
  bindQuery: ReturnType<typeof useInput>["bind"];
}> = ({ bindQuery }) => {
  const [isActive, setIsActive] = useState(false);

  const handleFocus = () => setIsActive(true);
  const handleBlur = () => setIsActive(false);

  return (
    <div
      className={`flex items-center w-72 h-12 mx-auto border rounded-full py-2 pl-4 pr-1 bg-white ${
        isActive ? "border-secondary" : "border-stroke-primary"
      }`}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="flex items-center gap-2 flex-1">
        <Search />
        <input
          type="text"
          className="text-sm text-primary focus:outline-none w-full placeholder-secondary"
          placeholder="Search"
          {...bindQuery}
        />
      </div>
    </div>
  );
};

const SearchBarHeader: React.FC<SearchBarHeaderProps> = () => {
  const activeTab = "all";
  const { value: query, bind: bindQuery } = useInput("");
  const api = useUnauthenticatedApi("STAY_DESTINATIONS");
  const operatorsApi = useUnauthenticatedApi("STAY_OPERATORS");
  const zoTripApi = useZoUnauthenticatedApi("BOOKING_TRIPS_INVENTORIES");
  const history = useHistory();

  const [isDropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [destinations, setDestinations] = useState<Array<LinkType>>([]);
  const [homes, setHomes] = useState<Array<LinkType>>([]);
  const [hostels, setHostels] = useState<Array<LinkType>>([]);
  const [plus, setPlus] = useState<Array<LinkType>>([]);
  const [trips, setTrips] = useState<Array<LinkType>>([]);
  const [house, setHouse] = useState<Array<LinkType>>([]);

  useEffect(() => {
    api.get("?fields=name,slug").then((res) => {
      let _destinatons: any[] = res.data.destinations;
      _destinatons.sort((a, b) => a.name.localeCompare(b.name));
      _destinatons = _destinatons.map((d) => {
        return {
          id: d.slug,
          destinationSlug: d.slug,
          title: d.name,
        };
      });
      setDestinations(_destinatons);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    zoTripApi.get("?limit=100").then((res) => {
      let _trips: any[] = res.data?.results;
      _trips.sort((a, b) => a.name.localeCompare(b.name));
      _trips = _trips.map((d) => {
        return {
          id: d?.pid,
          slug: `${slugify(d?.name || "")}-${d?.pid}`,
          title: d?.name,
        };
      });
      setTrips(_trips);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    operatorsApi
      .get("?operating_model=F&fields=name,slug,destination,type_code")
      .then((res) => {
        setHostels(
          formatLinks(
            res.data.operators.filter((k: GeneralObject) => k.type_code === "H")
          )
        );
        setPlus(
          formatLinks(
            res.data.operators.filter((k: GeneralObject) => k.type_code === "P")
          )
        );
        setHomes(
          formatLinks(
            res.data.operators.filter((k: GeneralObject) => k.type_code === "B")
          )
        );
        setHouse(
          formatLinks(
            res.data.operators.filter(
              (k: GeneralObject) => k.type_code === "HO"
            )
          )
        );
      })
      .catch(console.log);
  }, [operatorsApi]);

  const allResults = useMemo(
    () =>
      activeTab === "all"
        ? destinations
        : activeTab === "homes"
        ? homes
        : activeTab === "trips"
        ? trips
        : activeTab === "plus"
        ? plus
        : activeTab === "house"
        ? house
        : hostels,
    [activeTab, destinations, homes, hostels, plus, trips, house]
  );

  const queriedGroupResult = useMemo(() => {
    if (!query) return [];

    const searchResults = [
      { type: "destinations", results: destinations },
      { type: "trips", results: trips },
      { type: "hostel", results: hostels },
      { type: "homes", results: homes },
      { type: "plus", results: plus },
      { type: "house", results: house },
    ]
      .map(({ type, results }) => ({
        type,
        results: results.filter((result) =>
          result.title.toLowerCase().includes(query.toLowerCase())
        ),
      }))
      .filter(({ results }) => results.length > 0)
      .sort((a) => (a.type === activeTab ? -1 : 0));

    return searchResults;
  }, [activeTab, query, destinations, hostels, homes, plus, trips, house]);

  const queriedResult: Array<LinkType> | null = useMemo(() => {
    return allResults;
  }, [allResults]);

  const handleSearch = useCallback((operator: LinkType, type?: string) => {
    const eventData = {
      destination: operator.destinationSlug,
    };

    gtag("event", "SearchBar_BookNow_Click", eventData);
    trackZostelEvent("SearchBar_BookNow_Click", eventData);

    const destinationPath =
      type === "trips"
        ? `/trips/${operator.slug}`
        : type === "homes"
        ? `/homes/${operator.destinationSlug}`
        : type === "house"
        ? `/house/${operator.destinationSlug}`
        : `/zostel/${operator.destinationSlug}/`;

    setDropdownVisible(false);
    history.push(destinationPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputFocus = () => {
    setDropdownVisible(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropdownVisible(false);
    }
  };

  const renderResults = useMemo(() => {
    const renderResultItem = (result: any, type?: any) => (
      <li
        key={result.id}
        data-code={result.destinationSlug}
        data-name={result.title}
        className="p-2 text-sm text-primary cursor-pointer hover:text-orange"
        onMouseDown={(e) => {
          e.preventDefault();
          handleSearch(result, type);
        }}
      >
        {result.title}
      </li>
    );

    return query.length > 0 ? (
      queriedGroupResult?.length ? (
        queriedGroupResult.map((group) => (
          <div key={group.type}>
            <h3 className="text-xs font-medium text-gray-600 uppercase">
              {group.type}
            </h3>
            {group.results.map((result: any) => {
              return renderResultItem(result, group.type);
            })}
          </div>
        ))
      ) : (
        <li className="py-4 text-sm text-center text-text">Not found</li>
      )
    ) : queriedResult?.length ? (
      queriedResult.map((result) => renderResultItem(result))
    ) : (
      <li className="py-4 text-sm text-center text-text">Not found</li>
    );
  }, [query, queriedGroupResult, queriedResult, handleSearch]);

  return useMemo(
    () => (
      <div
        className="relative w-72 mx-auto"
        onBlur={handleBlur}
        onClick={handleInputFocus}
        tabIndex={0}
      >
        <SearchInput bindQuery={bindQuery} />
        {isDropdownVisible && (
          <div className="absolute z-40 w-full p-2 mt-2 rounded-2xl shadow-lg left-0 bg-white">
            <ul
              className="overflow-y-auto search-scrollbar"
              style={{
                maxHeight: "300px",
              }}
            >
              {renderResults}
            </ul>
          </div>
        )}
      </div>
    ),
    [bindQuery, isDropdownVisible, renderResults]
  );
};

export default SearchBarHeader;
